import { Rect } from "PosterEditor/model/poster-types";

export const snappingThreshold = 1;
const spacingThreshold = 0;

export interface SnapLine {
  orientation: "horizontal" | "vertical";
  side: "leading" | "trailing" | "center"; //Side on rect being moved dragged (passed in as `rect`).
  position: number;
  startPositionForRect: number; //The actual X or Y position to set your rect to to snap to the line.
  start: number;
  end: number;
  distance: number;
}

export const nearestVerticalSnapLine = (
  rect: Rect,
  rects: Rect[]
): SnapLine | undefined => {
  let nearestSnapLine: SnapLine | undefined = undefined;
  rects.forEach((r) => {
    const snapLine = verticalRectAlignment(rect, r);
    if (snapLine) {
      if (nearestSnapLine === undefined) {
        nearestSnapLine = snapLine;
      } else if (snapLine.distance < nearestSnapLine.distance) {
        nearestSnapLine = snapLine;
      }
    }
  });
  return nearestSnapLine;
};

export const nearestHorzontalSnapLine = (
  rect: Rect,
  rects: Rect[]
): SnapLine | undefined => {
  let nearestSnapLine: SnapLine | undefined = undefined;
  rects.forEach((r) => {
    const snapLine = horizontalRectAlignment(rect, r);
    if (snapLine) {
      if (nearestSnapLine === undefined) {
        nearestSnapLine = snapLine;
      } else if (snapLine.distance < nearestSnapLine.distance) {
        nearestSnapLine = snapLine;
      }
    }
  });
  return nearestSnapLine;
};

export const verticalRectAlignment = (
  rect1: Rect,
  rect2: Rect
): SnapLine | undefined => {
  // Figure out how long any snapline will need to be
  let start = Math.min(rect1.y, rect2.y);
  let end = Math.max(rect1.y + rect1.height, rect2.y + rect2.height);

  //Check center first
  const centerToCenterDistance = Math.abs(
    rect1.x + rect1.width / 2 - (rect2.x + rect2.width / 2)
  );
  if (centerToCenterDistance < snappingThreshold) {
    // This deals with Y rather than X because were aligning vertically (while were overlapping verticall).
    let x = rect2.x + rect2.width / 2;
    return {
      orientation: "vertical",
      startPositionForRect: rect2.x + rect2.width / 2 - rect1.width / 2,
      side: "center",
      position: x,
      start: start,
      end: end,
      distance: rect1.x + rect1.width / 2 - x,
    };
  }

  const leadingToLeading = Math.abs(rect1.x - rect2.x);
  if (leadingToLeading <= snappingThreshold) {
    let x = rect2.x;
    return {
      orientation: "vertical",
      startPositionForRect: rect2.x,
      side: "leading",
      position: x,
      start: start,
      end: end,
      distance: rect1.x + rect1.width / 2 - x,
    };
  }

  const trailingToTrailing = Math.abs(
    rect1.x + rect1.width - (rect2.x + rect2.width)
  );
  if (trailingToTrailing <= snappingThreshold) {
    let x = rect2.x + rect2.width;
    return {
      orientation: "vertical",
      startPositionForRect: x - rect1.width,
      side: "trailing",
      position: x,
      start: start,
      end: end,
      distance: rect1.x + rect1.width / 2 - x,
    };
  }

  //Check if rects overlap vertically
  let overlapsVertically =
    rect1.y + rect1.height >= rect2.y && rect2.y + rect2.height >= rect1.y;
  //If it overlaps vertically, any alignment must include the snapping threshold when
  //it's trailing-to-leading, or leading-to-trailing.
  //We ignore leading to leading and trailing to trailing as we don't want users
  //to overlap labels.
  if (overlapsVertically) {
    const leadingToTrailingDistance = Math.abs(
      rect1.x - (rect2.x + rect2.width)
    );

    if (leadingToTrailingDistance <= snappingThreshold) {
      let x = rect2.x + rect2.width + spacingThreshold;
      return {
        orientation: "vertical",
        side: "leading",
        position: x,
        startPositionForRect: x,
        start: start,
        end: end,
        distance: rect1.x - x,
      };
    }
    const trailingToLeadingDistance = Math.abs(
      rect2.x - (rect1.x + rect1.width)
    );
    if (trailingToLeadingDistance <= snappingThreshold) {
      let x = Math.abs(rect2.x);

      return {
        orientation: "vertical",
        startPositionForRect: x - rect1.width - spacingThreshold,
        side: "trailing",
        position: x,
        start: start,
        end: end,
        distance: rect1.x + rect1.width - rect2.x,
      };
    }
  } else {
    let start = Math.min(rect1.y, rect2.y);
    let end = Math.max(rect1.y + rect1.height, rect2.y + rect2.height);

    const leadingToTrailingDistance = Math.abs(
      rect1.x - (rect2.x + rect2.width)
    );

    if (leadingToTrailingDistance <= snappingThreshold) {
      let x = Math.abs(rect2.x + rect2.width);

      return {
        orientation: "vertical",
        side: "leading",
        startPositionForRect: x,
        position: x,
        start: start,
        end: end,
        distance: rect1.x - (rect2.x + rect2.width),
      };
    }
    const trailingToLeadingDistance = Math.abs(
      rect2.x - (rect1.x + rect1.width)
    );
    if (trailingToLeadingDistance <= snappingThreshold) {
      let x = Math.abs(rect2.x);

      return {
        orientation: "vertical",
        startPositionForRect: x - rect1.width,
        side: "trailing",
        position: x,
        start: start,
        end: end,
        distance: rect1.x + rect1.width - rect2.x,
      };
    }
    return undefined;
  }
};

export const horizontalRectAlignment = (
  rect1: Rect,
  rect2: Rect
): SnapLine | undefined => {
  let start = Math.min(rect1.x, rect2.x);
  let end = Math.max(rect1.x + rect1.width, rect2.x + rect2.width);

  //Check center first
  const centerToCenterDistance = Math.abs(
    rect1.y + rect1.height / 2 - (rect2.y + rect2.height / 2)
  );
  if (centerToCenterDistance < snappingThreshold) {
    // This deals with Y rather than X because were aligning vertically (while were overlapping verticall).
    let y = rect2.y + rect2.height / 2;
    return {
      orientation: "horizontal",
      startPositionForRect: rect2.y + rect2.height / 2 - rect1.height / 2,
      side: "center",
      position: y,
      start: start,
      end: end,
      distance: rect1.y + rect1.height / 2 - y,
    };
  }

  const leadingToLeading = Math.abs(rect1.y - rect2.y);
  if (leadingToLeading <= snappingThreshold) {
    let y = rect2.y;
    return {
      orientation: "vertical",
      startPositionForRect: rect2.y,
      side: "leading",
      position: y,
      start: start,
      end: end,
      distance: rect1.y + rect1.height / 2 - y,
    };
  }

  const trailingToTrailing = Math.abs(
    rect1.y + rect1.height - (rect2.y + rect2.height)
  );
  if (trailingToTrailing <= snappingThreshold) {
    let y = rect2.y + rect2.height;
    return {
      orientation: "vertical",
      startPositionForRect: y - rect1.height,
      side: "trailing",
      position: y,
      start: start,
      end: end,
      distance: rect1.y + rect1.height / 2 - y,
    };
  }

  //Check if rects overlap vertically
  let overlapsHorizontally =
    rect1.x + rect1.width >= rect2.x && rect2.x + rect2.width >= rect1.x;
  //If it overlaps vertically, any alignment must include the snapping threshold when
  //it's trailing-to-leading, or leading-to-trailing.
  //We ignore leading to leading and trailing to trailing as we don't want users
  //to overlap labels.
  if (overlapsHorizontally) {
    const leadingToTrailingDistance = Math.abs(
      rect1.y - (rect2.y + rect2.height)
    );
    if (leadingToTrailingDistance <= snappingThreshold) {
      let y = Math.abs(rect2.y + rect2.height + spacingThreshold);
      return {
        orientation: "horizontal",
        side: "leading",
        startPositionForRect: y,
        position: y,
        start: start,
        end: end,
        distance: rect1.x - y,
      };
    }
    const trailingToLeadingDistance = Math.abs(
      rect2.y - (rect1.y + rect1.height)
    );
    if (trailingToLeadingDistance <= snappingThreshold) {
      let y = Math.abs(rect2.y - spacingThreshold);
      return {
        orientation: "horizontal",
        side: "trailing",
        startPositionForRect: y - rect1.height,
        position: y,
        start: start,
        end: end,
        distance: rect1.y + rect1.height - y,
      };
    }
  } else {
    let start = Math.min(rect1.x, rect2.x);
    let end = Math.max(rect1.x + rect1.width, rect2.x + rect2.width);

    const leadingToTrailingDistance = Math.abs(
      rect1.y - (rect2.y + rect2.height)
    );
    if (leadingToTrailingDistance <= snappingThreshold) {
      let y = Math.abs(rect2.y + rect2.height);
      return {
        orientation: "vertical",
        side: "leading",
        startPositionForRect: y + spacingThreshold,
        position: y,
        start: start,
        end: end,
        distance: rect1.y - (rect2.y + rect2.height),
      };
    }
    const trailingToLeadingDistance = Math.abs(
      rect2.y - (rect1.y + rect1.height)
    );
    if (trailingToLeadingDistance <= snappingThreshold) {
      let y = Math.abs(rect2.y);
      return {
        orientation: "vertical",
        side: "trailing",
        startPositionForRect: y - rect1.height - spacingThreshold,
        position: y,
        start: start,
        end: end,
        distance: rect1.y + rect1.height - rect2.y,
      };
    }
    return undefined;
  }
};
